import { useCallback, useEffect, useState } from "react";

import AxiosFactory from "../services/http.service";
import ApiRoutes from "../config/api.routes";

// GET /relay/capabilities/:address — which allowlisted functions this wallet may relay, given
// its role in the backend's reconstructed org graph. The point is to decide what to render
// instead of probing the relay with requests and collecting 403s, and it is a better answer
// than User.role: the org graph knows about suspension, freezes and dismissal, and knows who
// the GTA is. Final authorization is still the contract's own on-chain rules.

export interface RelayCapability {
  // Allowlisted function name, e.g. "attestDeed".
  name: string;
  // Which relay target it lives on ("admin" | "core" | "bank" | "overcharge" | "governance" | "challenge").
  // Only the first four are honoured here — see RELAYABLE_TARGETS.
  target: string;
  address: string;
  requiredRole: string;
}

// F1 (docs/v10/BACKEND_V10_1_BRANCH_AUDIT.md, relay allowlist row): the backend at HEAD advertises
// raiseChallenge / respondToChallenge / withdrawChallenge / attest on target "challenge" and
// recordOutcome / judgePreMint on target "governance". Neither contract is ERC-2771 aware — both read
// raw `msg.sender` (EcoChallenge.sol:318-324,444-450,489,553; EcoFuturesGovernance.sol:378), so a
// call through EcoForwarder arrives with the *forwarder* as the actor: raiseChallenge fails
// `_requireIndependent(forwarder)`, respondToChallenge reverts NotTheChallengedVerifier,
// withdrawChallenge reverts NotAuthorised, and judgePreMint records the forwarder as the judge and
// misroutes the judgment fee. Those calls are direct wallet transactions in services/challenge.service
// and must never be enabled by this hook, whatever the backend says. Entries on any other target are
// dropped before `can()` sees them, so `can("recordOutcome")` and `can("raiseChallenge")` are false
// even against the mis-advertising backend; the ERC-2771 targets (Admin, Core, Bank, Overcharge) are
// unaffected.
const RELAYABLE_TARGETS = new Set(["admin", "core", "bank", "overcharge"]);

export const isRelayableCapability = (f: RelayCapability) => RELAYABLE_TARGETS.has(f.target);

export interface RelayCapabilities {
  address: string;
  // Org-graph role, or null for a wallet that is in no org (a plain patron).
  role: string | null;
  functions: RelayCapability[];
}

const useRelayCapabilities = (address?: string | null) => {
  const [capabilities, setCapabilities] = useState<RelayCapabilities | null>(null);
  const [loading, setLoading] = useState(false);
  // Set when the endpoint could not be reached at all — callers should fall back to whatever
  // local role guess they used before rather than hiding every action behind an outage.
  const [unavailable, setUnavailable] = useState(false);

  const refresh = useCallback(async () => {
    if (!address) {
      setCapabilities(null);
      setUnavailable(false);
      return;
    }
    setLoading(true);
    try {
      const axios = new AxiosFactory().axios;
      const data = await axios.get(ApiRoutes.relayCapabilities(address)).then((res) => res.data);
      // Filter here, once, so `capabilities.functions` and `can()` agree — a caller reading the
      // list directly never sees a non-relayable entry either.
      setCapabilities(
        data
          ? { ...data, functions: (Array.isArray(data.functions) ? data.functions : []).filter(isRelayableCapability) }
          : null
      );
      setUnavailable(!data);
    } catch {
      setCapabilities(null);
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, [address]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // True only when the backend positively lists the function ON A RELAYABLE TARGET (the list is
  // already filtered by isRelayableCapability). Callers that need to degrade gracefully should
  // check `unavailable` first — see SettlementPanel's canAttest.
  const can = useCallback(
    (fn: string) => !!capabilities?.functions?.some((f) => f.name === fn),
    [capabilities]
  );

  return { capabilities, can, unavailable, loading, refresh };
};

export default useRelayCapabilities;
